import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@apollo/client';
import { CircularProgress, Stack, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { GET_ONBOARDING_STATUS, ME } from '~/graphql/queries';
import { ROUTES } from '~/shared/constants';

/**
 * AuthCallback component handles the redirect after Auth0 authentication.
 * It queries the onboarding status and redirects the user to the appropriate page:
 * - If user doesn't exist → /consent (initial consent screen)
 * - If user exists but hasn't consented → /consent (initial consent screen)
 * - If user has consented but hasn't completed onboarding → /onboarding/consent (educational acknowledgment screen)
 * - If onboarding completed but person lacks name → /onboarding (new user onboarding form)
 * - If onboarding completed → /tree (main app)
 * - If query fails or not authenticated → / (login page as safe fallback)
 *
 * Note: Invitation data from Auth0's appState is extracted and stored in localStorage
 * by the Auth0Provider's onRedirectCallback handler.
 */
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();

  const {
    data: onboardingData,
    loading: isOnboardingLoading,
    error: onboardingError,
  } = useQuery(GET_ONBOARDING_STATUS, {
    skip: !isAuthenticated,
  });

  const {
    data: meData,
    loading: isMeLoading,
    error: meError,
  } = useQuery(ME, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (isAuth0Loading || isOnboardingLoading || isMeLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (onboardingError || meError) {
      navigate(ROUTES.LOGIN);
      return;
    }

    const status = onboardingData?.onboardingStatus;
    const me = meData?.me;

    if (!status) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (!status.userExists) {
      navigate(ROUTES.CONSENT);
      return;
    }

    // Guard: Onboarding not completed - no consent acknowledged
    // Redirect to consent screen
    if (!status.completed && !status.consentAcknowledged) {
      navigate(ROUTES.CONSENT);
      return;
    }

    // Guard: Onboarding not completed - consent acknowledged
    // Redirect to educational consent screen
    if (!status.completed && status.consentAcknowledged) {
      navigate(ROUTES.ONBOARDING.CONSENT);
      return;
    }

    // Guard: Onboarding completed but profile incomplete
    // Redirect to onboarding form
    const userPerson = me?.ownedTree?.persons?.find(
      (person) => person?.userId === me.id,
    );

    if (userPerson && (!userPerson.firstName || !userPerson.lastName)) {
      navigate(ROUTES.ONBOARDING.ROOT);
      return;
    }

    navigate(ROUTES.TREE);
  }, [
    isAuthenticated,
    isAuth0Loading,
    isOnboardingLoading,
    isMeLoading,
    onboardingError,
    meError,
    onboardingData,
    meData,
    navigate,
  ]);

  return (
    <LoadingContainer
      direction="row"
      role="status"
      aria-busy="true"
      aria-label="Signing in"
    >
      <LoadingContent>
        <CircularProgress size={48} aria-hidden="true" />
      </LoadingContent>
    </LoadingContainer>
  );
};

const LoadingContainer = styled(Stack)({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});

const LoadingContent = styled(Stack)({
  alignItems: 'center',
  gap: theme.spacing(2),
});
